import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {msg} from '@lit/localize';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {conditional} from '../../../util/conditional';
import store from '../../../store';
import {filterAndJoin} from '../../../util/filterAndJoin';

import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {
	selectIsOnesided,
	selectIsSingle,
	selectSystemLumen,
	selectSystemLumenPerPower,
	selectSystemLumenString,
	selectSystemPower,
	selectSystemPowerString,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectRegion
} from '../../../selectors';

@customElement('line-technical-data')
export class LineTechnicalData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}

			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.lang = state.status.lang;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.shortNumberProductModel = state.shortNumber.data.ProductModel ?? '';
		this.isOnsided = selectIsOnesided(state);
	}

	t(attributeKey, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, attributeKey, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, attributeKey, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}


	render() {
		const s = this.state;
		const shouldDisplayFeature = (value) => value !== undefined && value !== '';
		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV');
		const isDescAttPresent = descAtt !== undefined;

		this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV');

		const applicableStandards = [
			this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV'),
			this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV'),
			this.t('root::Heads::Head::EBlock::EB_ElectricalProtectionClass', 'FV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV'),
			this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV'),
		];
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;

		return html`

			${shouldDisplayFeature(this.t('shared::ProductType::HS_HousingType', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Modell -->
						<trans-label slot="label" key="shared::ProductType::HS_HousingType" context="F"></trans-label>
						<trans-label slot="value" key="shared::ProductType::HS_HousingType" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Lichtfarbe -->
						<trans-label slot="label" key="root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature" context="F"></trans-label>
						<span slot="value">
										<trans-label key="root::Heads::Head::Type::Luminaire::LightEngine::LE_ColourTemperature" context="FV.S" prefix="LED "></trans-label><br>
										<trans-label key="root::Heads::Head_2::Type::Luminaire::LightEngine::LE_ColourTemperature" context="FV.S" prefix="LED "></trans-label>
									</span>
					</shared-specsheet-feature>` : ''}


			<shared-specsheet-feature>
				<!-- Systemlumen -->
				<trans-label slot="label" key="LU_SystemLuminousFlux" context="D"></trans-label>
				<span slot="value">
					${selectSystemLumen(s) + ' lm'}
					${!selectIsOnesided(s) ? '(' + selectSystemLumenString(s) + ' lm)' : null}
				</span>
			</shared-specsheet-feature>


			${isDescAttPresent ? html`
				<shared-specsheet-feature>
					<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
					<span slot="value" class="value">${descAtt}</span>
				</shared-specsheet-feature>` : ''}

			<shared-specsheet-feature>
				<!-- Systemleistung -->
				<trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label>
				<span slot="value">
								${selectSystemPower(s)} <trans-label key="LU_SystemWattageUnit" context="D"></trans-label>
								${!selectIsOnesided(s) ? '(' + selectSystemPowerString(s) + ' ' + this.t('LU_SystemWattageUnit', 'D') + ')' : null}
							</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature>
				<!-- Lichtausbeute -->
				<trans-label slot="label" key="LU_Efficacy" context="D"></trans-label>
				<span slot="value">${selectSystemLumenPerPower(s)} <trans-label key="LU_EfficacyUnit" context="D"></trans-label></span>
			</shared-specsheet-feature>


			${when(!selectIsSingle(s),
				() => html`
					<shared-specsheet-feature>
						<!-- Lichtverteilung -->
						<trans-label slot="label" key="root::Poles::Pole::SpecialLine::HS_Construction" context="F"></trans-label>
						<trans-label slot="value" key="root::Poles::Pole::SpecialLine::HS_Construction" context="FV.S"></trans-label>
					</shared-specsheet-feature>
				`,
				() => html`
				`)}

			<shared-specsheet-feature>
				<trans-label slot="label" key="LE_LightEngineDescription" context="D"></trans-label>
				<span slot="value">
							${filterAndJoin([
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.S'),
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.S'),
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_OpticalVersion', 'FV.S'),
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_ReflectorColour', 'FV.S'),
							], ', ')}

							${when(!selectIsOnesided(s), () => html`<br>
							${filterAndJoin([
								this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_LightEngineModuleSize', 'FV.S'),
								this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_LightDistribution', 'FV.S'),
								this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_OpticalVersion', 'FV.S'),
								this.t('root::Heads::Head_2::Type::Luminaire::LightEngine::LE_ReflectorColour', 'FV.S'),
							], ', ')}
							`)}
						</span>
			</shared-specsheet-feature>

			${shouldDisplayFeature(this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEmission_Uplight', 'FV.S')) ?
				html`
					<shared-specsheet-feature>" >
						<trans-label slot="label" key="root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEmission_Uplight" context="F"></trans-label>
						<span slot="value">
							${filterAndJoin([
								this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEmission_Uplight', 'FV.S'),
								selectRegion(s) === 'fra' ? this.t('root::Heads::Head::Type::Luminaire::LightEngine::LE_LightEmission_Uplight', 'FV.S') : ''
							], ', ')}
						</span>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.c('EB_EBlockOperatingGearDescription.ElectronicConverter"', 'DV')) ?
				html`
					<shared-specsheet-feature>
						<!-- Elektrische Ausführung -->
						<trans-label slot="label" key="EB_EBlockOperatingGearDescription" context="D"></trans-label>
						<trans-label slot="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="DV"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.c('LU_SurgeProtection.10kV"', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Überspannungsschutz -->
						<trans-label slot="label" key="LU_SurgeProtection" context="D"></trans-label>
						<trans-label slot="value" key="LU_SurgeProtection.10kV" context="DV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.t('root::Zhaga::HS_HousingZhaga', 'FV')) ?
				html`
					<shared-specsheet-feature>
						<!-- Zhaga -->
						<trans-label slot="label" key="root::Zhaga::HS_HousingZhaga" context="F"></trans-label>
						<trans-label slot="value" key="root::Zhaga::HS_HousingZhaga" context="FV"></trans-label>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.t('root::Heads::Head::EBlock::EB_Control', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Steuerung -->
						<trans-label slot="label" key="Heads::Head::EBlock::EB_Control" context="F"></trans-label>
						<trans-label slot="value" key="Heads::Head::EBlock::EB_Control" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('root::Zhaga::HS_HousingZhaga', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Zhaga -->
						<trans-label slot="label" key="root::Zhaga::HS_HousingZhaga" context="F"></trans-label>
						<trans-label slot="value" key="root::Zhaga::HS_HousingZhaga" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('root::Heads::Head::EBlock::EB_ConstantLumenTechnology', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- CLT -->
						<trans-label slot="label" key="Heads::Head::EBlock::EB_ConstantLumenTechnology" context="F"></trans-label>
						<trans-label slot="value" key="Heads::Head::EBlock::EB_ConstantLumenTechnology" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.c('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Frequenz/Spannung -->
						<trans-label slot="label" key="LU_TechnicalRegion" context="D"></trans-label>
						<trans-label slot="value" key="LU_TechnicalRegion.220-240V 50/60 Hz" context="DV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('root::Heads::Head::ColourCoatingBase::HS_HousingColour', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gehäusefarbe -->
						<trans-label slot="label" key="root::Heads::Head::ColourCoatingBase::HS_HousingColour" context="F"></trans-label>
						<trans-label slot="value" key="root::Heads::Head::ColourCoatingBase::HS_HousingColour" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial') ? html`
				<shared-specsheet-feature>
					<!-- Gehäuse -->
					<trans-label slot="label" key="LU_LuminaireMaterial" context="D"></trans-label>
					<trans-label slot="value" .key="${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial')}"
								 context="DV.S"></trans-label>
				</shared-specsheet-feature>
			` : ''}

			${shouldDisplayFeature(this.t('root::Heads::Head::ColourCoatingBase::HS_HousingCoating', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Oberfläche -->
						<trans-label slot="label" key="root::Heads::Head::ColourCoatingBase::HS_HousingCoating" context="F"></trans-label>
						<trans-label slot="value" key="root::Heads::Head::ColourCoatingBase::HS_HousingCoating" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('root::Poles::Pole::Basic::HS_Base', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gründung -->
						<trans-label slot="label" key="root::Poles::Pole::Basic::HS_Base" context="F"></trans-label>
						<trans-label slot="value" key="root::Poles::Pole::Basic::HS_Base" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			<shared-specsheet-feature>
				<trans-label slot="label" key="Heads::Head::Housing::HS_HousingPowerConnection" context="F"></trans-label>
				<!-- Ausstattung -->
				${when(!selectIsSingle(s),
					() => html`
						<span slot="value">
									<trans-label key="root::Poles::Pole::Basic::HS_NumberPoleDoors" context="FV.S"></trans-label><br>
									<trans-label key="root::Poles::Pole::SpecialLine::HS_HousingMountingLocationEBlock_1" context="F"></trans-label>: <trans-label key="Heads::Head::EBlock::EB_EBlockMountingType" context="FV.S"></trans-label>
									${when(!selectIsOnesided(s), () => html`
										<br>
										<trans-label key="root::Poles::Pole::SpecialLine::HS_HousingMountingLocationEBlock_2" context="F"></trans-label>:
										<trans-label key="Heads::Head_2::EBlock::EB_EBlockMountingType" context="FV.S"></trans-label>
									`)}
									<br>
									<trans-label .key="${this.c('DescriptiveAttributeReference.LU_FuseBox')}" context="DV.S"></trans-label>
								</span>
					`,
					() => html`
						<span slot="value">
									<trans-label key="Heads::Head::Housing::HS_HousingPowerConnection" context="FV.S"></trans-label>
								</span>
					`
				)}
			</shared-specsheet-feature>


			${shouldDisplayFeature(this.t('root::Poles::Pole::Basic::HS_Height', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Höhe -->
						<trans-label slot="label" key="root::Poles::Pole::Basic::HS_Height" context="F"></trans-label>
						<trans-label slot="value" key="root::Poles::Pole::Basic::HS_Height" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			${(this.t('HS_HousingDiameter.114', 'DV.S') !== '' || this.t('HS_HousingDiameterUnit.MM', 'DV.S') !== '') ?
				html`
					<shared-specsheet-feature>
						<!-- Durchmesser -->
						<trans-label slot="label" key="HS_HousingDiameter" context="D"></trans-label>
						<span slot="value">
							114
							<!--<trans-label key="HS_HousingDiameter.114" context="DV.S"></trans-label> -->
							<trans-label key="HS_HousingDiameterUnit.MM" context="DV.S"></trans-label>
						</span>
					</shared-specsheet-feature>
				` : ''}


			${shouldDisplayFeature(this.c('LU_LuminaireWeightUnit.KG', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gewicht -->
						<trans-label slot="label" key="LU_LuminaireWeight" context="D"></trans-label>
						<span slot="value" class="specsheet__value">
							${selectWeight(s)}
							<trans-label key="LU_LuminaireWeightUnit.KG" context="DV.S"></trans-label><br>
						</span>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(selectWindsurfacearea(s)) ?
				html`
					<shared-specsheet-feature>
						<!-- Windangriffsfläche -->
						<trans-label slot="label" key="HS_WindSurfaceArea" context="D"></trans-label>
						<span slot="value">
							${selectWindsurfacearea(s)} m<sup>2</sup>
							<!-- <trans-label key="HS_WindSurfaceAreaUnit.M2" context="DV.S"></trans-label> -->
						</span>
					</shared-specsheet-feature>` : ''}

			${isStandardsPresent ? html`
				<shared-specsheet-feature>
					<trans-label slot="label" class="label" key="LU_ApplicableStandards" context="D"></trans-label>
					<span slot="value" class="value">
							${filterAndJoin(filteredStandards, ', ')}
					</span>
				</shared-specsheet-feature>` : ''}

			${this.shortNumberProductModel ? html`
				<shared-specsheet-feature>
					<span slot="label" class="label">${msg('Model ID')}</span>
					<span slot="value" class="value">
							${this.shortNumberProductModel}
					</span>
				</shared-specsheet-feature>` : ''}


        `;
	}
}
